/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React from 'react'
import { FormattedMessage } from 'react-intl'

import { EuiContextMenuItem, EuiIcon } from '@elastic/eui'

import type { AnyAdminProject } from '@modules/project-admin-api/types'
import { useRestoreProjectMutation } from '@modules/project-admin-lib/hooks/restore'
import { addToast } from '@modules/cui/Toasts'
import usePermissions from '@modules/permissions-lib/hooks'

export type Props = {
  project: AnyAdminProject
  onComplete: () => void
}

const RestoreProjectButton: React.FunctionComponent<Props> = ({ project, onComplete }) => {
  const { hasPermissions: hasRestoreProjectPermissions } = usePermissions([
    {
      action: 'restore',
      type: `project-${project.type}`,
      id: project.id,
    },
  ])

  const restoreProjectMutation = useRestoreProjectMutation(project.type)

  return (
    <EuiContextMenuItem
      disabled={!hasRestoreProjectPermissions}
      icon={<EuiIcon type='refresh' size='m' />}
      onClick={() => {
        restoreProjectMutation.mutate({ id: project.id }, { onSuccess })
      }}
    >
      <FormattedMessage id='project.restoreButton' defaultMessage='Restore project' />
    </EuiContextMenuItem>
  )

  function onSuccess() {
    addToast({
      family: 'project.restore',
      color: 'success',
      title: <FormattedMessage id='project.restore.success' defaultMessage='Project restored' />,
    })
    onComplete()
  }
}

export default RestoreProjectButton
